import { createTheme, rem, type MantineTheme } from "@mantine/core";
import { IconFileUpload } from "./lib/hugeiconsTablerCompat";

/** Consistent rounding (px) for inputs, buttons, and form-adjacent controls */
export const UI_FIELD_RADIUS_PX = 12;
const FIELD_RADIUS = UI_FIELD_RADIUS_PX;

/** Modal / dialog surfaces (popup modals) */
const MODAL_RADIUS_PX = 16;

/** Segmented control inner pills — tighter than text fields */
const SEGMENTED_CONTROL_RADIUS_PX = 8;

/** Capsule pills for Badge / Pill (reference: soft tags with full rounding) */
const TAG_PILL_RADIUS = rem(9999);

/**
 * High-contrast light tags: pale fill, bold hue-matched text/icon, subtle outline.
 * Aligns with reference palette: orange, blue, purple, green, gray (+ red for errors).
 */
const TAG_SURFACE = {
  orange: { bg: "#FFF1E6", fg: "#C2410C", border: "#FDBA94" },
  blue: { bg: "#E8F1FF", fg: "#1D4ED8", border: "#93C5FD" },
  violet: { bg: "#F3EEFF", fg: "#6D28D9", border: "#C4B5FD" },
  green: { bg: "#DCFCE7", fg: "#047857", border: "#6EE7B7" },
  gray: { bg: "#F1F5F9", fg: "#334155", border: "#CBD5E1" },
  red: { bg: "#FEE2E2", fg: "#B91C1C", border: "#FCA5A5" },
} as const;

/** Maps Mantine `color` prop (base name) → reference tag palette */
const TAG_COLOR_MAP: Record<string, keyof typeof TAG_SURFACE> = {
  orange: "orange",
  blue: "blue",
  violet: "violet",
  grape: "violet",
  green: "green",
  teal: "green",
  gray: "gray",
  red: "red",
  yellow: "orange",
  brand: "violet",
  indigo: "blue",
  cyan: "blue",
};

/** Mantine Badge has no `data-color` on DOM; override via `styles` using props. */
function dashboardTagLightStyle(
  theme: MantineTheme,
  color: unknown,
  variant: string | undefined,
) {
  if (variant !== "light") return {};
  const raw = String(color ?? theme.primaryColor);
  const baseName = raw.split(".")[0] ?? "";
  const surfaceKey = TAG_COLOR_MAP[baseName];
  if (!surfaceKey) return {};
  const t = TAG_SURFACE[surfaceKey];
  return {
    backgroundColor: t.bg,
    color: t.fg,
    border: `${rem(1)} solid ${t.border}`,
    boxSizing: "border-box" as const,
  };
}

/** All floating dropdown / popover surfaces (Menu, Select, dates, etc.) */
const DROPDOWN_RADIUS = rem(12);

const theme = createTheme({
  primaryColor: "brand",
  primaryShade: 6,
  black: "#0D0D27",
  colors: {
    brand: [
      "#eff1ff",
      "#dfe3ff",
      "#c4cbff",
      "#a7b0ff",
      "#8c97ff",
      "#7280ff",
      "#5363FF",
      "#4553dc",
      "#3945b8",
      "#2e3896",
    ],
    gray: [
      "#F2F2F4",
      "#ebebee",
      "#E3E3E5",
      "#d7d9de",
      "#c2c6cf",
      "#9ca3b1",
      "#6B7383",
      "#515a6b",
      "#3a4253",
      "#252b3a",
    ],
  },
  fontSmoothing: true,
  fontFamily: "Inter",
  headings: { fontFamily: "Inter Tight" },
  components: {
    Card: {
      defaultProps: {
        padding: "md",
        shadow: "none",
        radius: "lg",
        withBorder: true,
      },
      styles: {
        root: {
          transition: "box-shadow 150ms ease",
          "&:hover": {
            boxShadow: "var(--mantine-shadow-sm)",
          },
        },
      },
    },
    Paper: {
      defaultProps: {
        shadow: "none",
      },
    },
    Flex: {
      defaultProps: {
        gap: "md",
      },
    },
    Badge: {
      defaultProps: {
        variant: "light",
        size: "lg",
      },
      styles: (
        theme: MantineTheme,
        props: { color?: unknown; variant?: string },
      ) => ({
        root: {
          borderRadius: TAG_PILL_RADIUS,
          fontWeight: 600,
          paddingInline: rem(16),
          paddingBlock: rem(7),
          minHeight: rem(34),
          ...dashboardTagLightStyle(theme, props.color, props.variant),
        },
        section: {
          color: "inherit",
          display: "flex",
          alignItems: "center",
        },
        label: {
          fontSize: rem(14),
          lineHeight: 1.35,
          textTransform: "none",
          color: "inherit",
        },
      }),
    },
    Button: {
      defaultProps: {
        radius: FIELD_RADIUS,
        size: "md",
      },
      styles: {
        root: { fontWeight: 500 },
        label: { fontSize: rem(14) },
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: FIELD_RADIUS,
        size: "md",
      },
    },
    CloseButton: {
      defaultProps: {
        radius: FIELD_RADIUS,
        size: "md",
      },
    },
    SegmentedControl: {
      defaultProps: {
        radius: SEGMENTED_CONTROL_RADIUS_PX,
        size: "md",
      },
    },
    Pagination: {
      defaultProps: {
        size: "md",
      },
    },
    Avatar: {
      defaultProps: {
        radius: "xl",
        size: "lg",
      },
    },
    Fieldset: {
      defaultProps: {
        radius: "lg",
        variant: "filled",
      },
    },
    Blockquote: {
      defaultProps: {
        radius: "lg",
      },
    },
    Alert: {
      defaultProps: {
        radius: "lg",
        variant: "light",
      },
    },
    TextInput: {
      defaultProps: {
        size: "md",
        radius: FIELD_RADIUS,
      },
    },
    PasswordInput: {
      defaultProps: {
        size: "md",
        radius: FIELD_RADIUS,
      },
    },
    NumberInput: {
      defaultProps: {
        size: "md",
        radius: FIELD_RADIUS,
      },
    },
    Textarea: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
    },
    FileInput: {
      defaultProps: {
        size: "md",
        radius: FIELD_RADIUS,
        leftSection: <IconFileUpload size={18} />,
        leftSectionPointerEvents: "none",
      },
      styles: {
        input: {
          backgroundColor: "#F2F2F4",
          border: "1px dashed #c2c6cf",
        },
      },
    },
    ColorInput: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
      styles: {
        dropdown: { borderRadius: DROPDOWN_RADIUS },
      },
    },
    Pill: {
      defaultProps: {
        size: "lg",
      },
      styles: {
        root: {
          borderRadius: TAG_PILL_RADIUS,
          fontWeight: 600,
        },
        label: {
          fontSize: rem(14),
          textTransform: "none",
        },
      },
    },
    NavLink: {
      defaultProps: {
        variant: "filled",
        radius: FIELD_RADIUS,
      },
      styles: (theme: MantineTheme) => ({
        root: {
          borderRadius: rem(FIELD_RADIUS),
          "&:not([data-active]):not([aria-current='page']):not([data-disabled])": {
            color: theme.colors.gray[7],
          },
        },
      }),
    },
    Checkbox: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
    },

    Popover: {
      defaultProps: {
        shadow: "none",
      },
      styles: {
        dropdown: { borderRadius: DROPDOWN_RADIUS },
      },
    },
    HoverCard: {
      defaultProps: {
        shadow: "none",
      },
      styles: {
        dropdown: { borderRadius: DROPDOWN_RADIUS },
      },
    },
    Combobox: {
      styles: {
        dropdown: { borderRadius: DROPDOWN_RADIUS },
      },
    },
    Select: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
      styles: {
        dropdown: { borderRadius: DROPDOWN_RADIUS },
        input: { borderRadius: FIELD_RADIUS },
      },
    },
    MultiSelect: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
      styles: {
        dropdown: { borderRadius: DROPDOWN_RADIUS },
        input: { borderRadius: FIELD_RADIUS },
      },
    },
    Autocomplete: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
      styles: {
        dropdown: { borderRadius: DROPDOWN_RADIUS },
      },
    },
    TagsInput: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
      styles: {
        dropdown: { borderRadius: DROPDOWN_RADIUS },
      },
    },
    Menu: {
      defaultProps: {
        shadow: "none",
      },
      styles: {
        dropdown: { borderRadius: DROPDOWN_RADIUS },
        item: { borderRadius: rem(FIELD_RADIUS) },
      },
    },
    /** @mantine/dates — theme keys merged at runtime */
    TimeInput: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
    },

    Input: {
      defaultProps: {
        radius: FIELD_RADIUS,
      },
    },
    Modal: {
      defaultProps: {
        closeOnClickOutside: false,
        centered: true,
        radius: MODAL_RADIUS_PX,
        padding: "lg",
      },
      styles: {
        title: { fontWeight: 700 },
        content: { boxShadow: "none" },
      },
    },
    Divider: {
      styles: {
        root: {
          "--divider-color": "#E3E3E5",
        },
        label: {
          color: "#515a6b",
          fontWeight: 600,
          fontSize: "14px",
        },
      },
    },
    AppShell: {
      styles: {
        header: {
          backgroundColor: "#FFFFFF",
          fontWeight: 600,
          fontSize: "14px",
          marginLeft: "25px",
          marginRight: "25px",
          marginTop: "10px",
          borderRadius: "30px",
        },
        navbar: {
          marginLeft: "10px",
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          border: "1px solid #E3E3E5",
          top: "calc(var(--app-shell-header-offset, 0rem) + 10px)",
          bottom: "10px",
          height: "auto",
        },
        footer: {
          backgroundColor: "#FFFFFF",
          borderRadius: "30px",
          marginLeft: "25px",
          marginRight: "25px",
          marginBottom: "10px",
        },
        main: {
          backgroundColor: "#F4F4F7",
          color: "#0D0D27",
        },
      },
    },
  },
});

export default theme;
